"use client";

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { platformApi } from '@/services/platformApi';
import { analyticsService } from '@/services/analyticsService';

// Landing page components
import AuthenticationSection from '@/components/landing/AuthenticationSection';
import ProspectiveUserChat from '@/components/landing/ProspectiveUserChat';
import MarketingSection from '@/components/landing/MarketingSection';
import AtomicCommerceSection from '@/components/landing/AtomicCommerceSection';
import SecurityTokenLandingSection from '@/components/landing/SecurityTokenLandingSection';
import Footer from '@/components/shared/Footer';
import {
  isCommerceLandingHost,
  isBotsLandingHost,
  getDefaultAuthenticatedDestination,
} from '@/utils/domainDetection';
import BotsLandingPage from './BotsLandingPage';

const LandingPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user, loading: authLoading } = useAuth();
  
  // Get redirect URL from query parameters
  const redirectUrl = searchParams.get('redirect');

  // If user is already logged in, redirect to hostname default (not on bots.vaiu.app)
  useEffect(() => {
    if (isBotsLandingHost()) return;
    if (!authLoading && user) {
      navigate(getDefaultAuthenticatedDestination());
    }
  }, [user, authLoading, navigate]);



  const handleGuestLogin = async () => {
    setError('');
    setLoading(true);

    try {
      console.log('👤 Starting guest login process...');
      
      // Add timeout to prevent infinite loading (15 seconds)
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Guest login timeout - server not responding')), 15000)
      );
      
      // Race between API call and timeout
      const result = await Promise.race([
        platformApi.guestLogin(),
        timeoutPromise
      ]) as any;
      
      console.log('✅ Guest login successful, result:', result);

      if (result.token) {
        console.log('🔑 Guest token received, logging in...');
        
        // Track guest login in analytics (if available)
        if (typeof analyticsService !== 'undefined' && analyticsService.trackGuestLogin) {
          try {
            analyticsService.trackGuestLogin(result.username, result.sessionExpiry);
          } catch (analyticsError) {
            console.warn('Analytics tracking failed:', analyticsError);
            // Don't fail the login if analytics fails
          }
        }
        
        // Pass isGuestLogin flag and guest data to handle guest login differently
        await login(result.token, true, {
          username: result.username,
          userId: result.userId,
          sessionExpiry: result.sessionExpiry
        });
        
        // Navigate to dashboard with guest indicator
        const destination = redirectUrl || getDefaultAuthenticatedDestination();
        console.log('🏠 Navigating guest to:', destination);
        navigate(destination, { replace: true, state: { isGuest: true } });
      } else {
        throw new Error('No authentication token received for guest user');
      }
    } catch (err: any) {
      console.error('❌ Guest login error:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        response: err.response,
        status: err.status,
        name: err.name,
        code: err.code
      });
      
      let errorMessage = err.message || 'Guest login failed';
      
      // More specific error messages
      if (errorMessage.includes('timeout')) {
        errorMessage = 'The server took too long to respond. Please try again.';
      } else if (errorMessage.includes('Network error') || errorMessage.includes('fetch')) {
        errorMessage = 'Cannot connect to server. Please check if the backend is running.';
      } else if (errorMessage.includes('401') || errorMessage.includes('403')) {
        errorMessage = 'Guest login is not authorized. Check API key configuration.';
      } else if (errorMessage.includes('404')) {
        errorMessage = 'Guest login endpoint not found. Backend may need updating.';
      } else if (errorMessage.includes('500') || errorMessage.includes('Internal')) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      setError(errorMessage);
      
      // Show the error in the UI
      alert(`Guest Login Error: ${errorMessage}\n\nCheck browser console for details.`);
    } finally {
      setLoading(false);
    }
  };

  // Handle sign in click from AuthenticationSection
  const handleSignInClick = () => {
    navigate('/login');
  };

  if (isBotsLandingHost()) {
    return <BotsLandingPage />;
  }

  const showCommerceLanding = isCommerceLandingHost();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Login / signup / guest — always at top */}
      <AuthenticationSection 
        onGuestClick={handleGuestLogin} 
        onSignInClick={handleSignInClick}
      />

      {showCommerceLanding ? (
        <>
          {/* atomicMarketplace.com — full commerce landing */}
          <div>
            <AtomicCommerceSection />
            <ProspectiveUserChat />
            <MarketingSection />
          </div>
        </>
      ) : (
        /* vaiu.app & bank apps — compact security + token focus */
        <SecurityTokenLandingSection />
      )}

      <Footer />
    </div>
  );
};

export default LandingPage;